import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Business from '@mui/icons-material/Business';
import Person from '@mui/icons-material/Person';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { FingoDivider } from '../../../components/dividers';
import { PaperHeader } from '../../../components/headers';
import { FingoSnackBar } from '../../../components/snackBars';
import ProfileInfoGrid from '../../../components/profile/ProfileInfoGrid';
import ProfileEditButtons from '../../../components/profile/ProfileEditButtons';
import ToggleEditButton from '../../../components/profile/ToggleEditButton';
import useProfileInfoForm from '../../../hooks/useProfileInfoForm';
import { DEFAULT_ICON_CLASS_NAME } from '../../../theme/constants/icon';

const TITLE = 'Mi información';
const DESCRIPTION = 'Esta es tu información y la de tu empresa que serán necesarias para operar con nosotros';

const MyInfoView = ({
  useProfileInfo,
  canEdit,
  showPaperHeader,
  showEditButtonsAlways,
  stackSx,
}) => {
  const topRef = useRef(null);
  const {
    userObject,
    formValues,
    setFormValues,
    errorMsg,
    setErrorMsg,
    disabledEdit,
    loading,
    snackbarOpen,
    snackbarMessage,
    toggleSnackbar,
    handleOnClick,
    handleCancel,
    disableSaveButton,
    setAttributeChanges,
    initializeFormValues,
    selectedCompany,
  } = useProfileInfoForm();

  const profileInfo = useProfileInfo({ setAttributeChanges });

  useEffect(() => {
    initializeFormValues(profileInfo);
  }, [selectedCompany, userObject]);

  const userInfo = useMemo(
    () => profileInfo.filter((grid) => grid.category === 'user'),
    [profileInfo],
  );
  const companyInfo = useMemo(
    () => profileInfo.filter((grid) => grid.category === 'company'),
    [profileInfo],
  );

  const info = [
    { title: 'Información personal:', data: userInfo, icon: <Person className={DEFAULT_ICON_CLASS_NAME} /> },
    { title: 'Información de la empresa:', data: companyInfo, icon: <Business className={DEFAULT_ICON_CLASS_NAME} /> },
  ];

  const showBottomEditButtons = showEditButtonsAlways || (canEdit && !disabledEdit);

  return (
    <>
      <Stack sx={stackSx}>
        {showPaperHeader ? (
          <>
            <PaperHeader viewTitle={TITLE} />
            <FingoDivider />
            <Typography paragraph sx={{ mt: 2, mb: 4 }}>
              {DESCRIPTION}
            </Typography>
          </>
        ) : (
          <Stack direction="row" alignItems="center" spacing={1} mb={4}>
            <Stack direction="column">
              <Typography variant="large_3" color="text.primary" fontWeight={700}>
                {TITLE}
              </Typography>
              <Typography variant="small_3">
                {DESCRIPTION}
              </Typography>
            </Stack>
            {canEdit && (
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <ToggleEditButton
                  disabledEdit={disabledEdit}
                  onClick={() => handleOnClick(topRef)}
                  onCancel={handleCancel}
                />
              </Box>
            )}
          </Stack>
        )}
        <Stack spacing={2} ref={topRef}>
          <ProfileInfoGrid
            info={info}
            user={userObject}
            disabledEdit={disabledEdit}
            formValues={formValues}
            setFormValues={setFormValues}
            setErrorMsg={setErrorMsg}
            errorMsg={errorMsg}
          />
          {showBottomEditButtons && (
            <ProfileEditButtons
              disabledEdit={disabledEdit}
              loading={loading}
              disableSaveButton={disableSaveButton}
              onEdit={() => handleOnClick(topRef)}
              onCancel={handleCancel}
            />
          )}
        </Stack>
      </Stack>
      <FingoSnackBar
        open={snackbarOpen}
        setOpen={toggleSnackbar}
        message={snackbarMessage}
        severity="error"
      />
    </>
  );
};

MyInfoView.propTypes = {
  useProfileInfo: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  showPaperHeader: PropTypes.bool,
  showEditButtonsAlways: PropTypes.bool,
  stackSx: PropTypes.shape({}),
};

MyInfoView.defaultProps = {
  canEdit: true,
  showPaperHeader: false,
  showEditButtonsAlways: false,
  stackSx: { mt: 0, pb: 5 },
};

export default MyInfoView;
